// src/components/TimePicker.js - Reusable 12-hour time picker component

import React from 'react';

const TimePicker = ({ value, onChange, label, required = false, className = '' }) => {
  // Parse existing value (comes in as "HH:MM" 24-hour format)
  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: '', minute: '', period: 'AM' };

    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;

    return {
      hour: hour12.toString(),
      minute: m.toString().padStart(2, '0'),
      period
    };
  };

  const { hour, minute, period } = parseTime(value);

  // Convert back to 24-hour format for storage
  const handleChange = (newHour, newMinute, newPeriod) => {
    if (!newHour || newMinute === '') return;

    let hour24 = parseInt(newHour);
    if (newPeriod === 'PM' && hour24 !== 12) hour24 += 12;
    if (newPeriod === 'AM' && hour24 === 12) hour24 = 0;

    const timeStr = `${hour24.toString().padStart(2, '0')}:${newMinute.padStart(2, '0')}`;
    onChange(timeStr);
  };

  // Hour options: 1-12
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  // Minute options: 00, 05, 10, ... 55
  const minutes = Array.from({ length: 12 }, (_, i) =>
    (i * 5).toString().padStart(2, '0')
  );

  return (
    <div className={className}>
      {label && (
        <label className="block text-slate-700 text-sm font-medium mb-1">
          {label} {required && '*'}
        </label>
      )}
      <div className="flex gap-1 items-center">
        {/* Hour */}
        <select
          value={hour}
          onChange={(e) => handleChange(e.target.value, minute || '00', period)}
          className="pl-2 pr-6 py-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none bg-white"
          required={required}
        >
          <option value="">--</option>
          {hours.map(h => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>

        <span className="text-slate-500 font-bold">:</span>

        {/* Minute */}
        <select
          value={minute}
          onChange={(e) => handleChange(hour || '12', e.target.value, period)}
          className="pl-2 pr-6 py-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none bg-white"
          required={required}
        >
          <option value="">--</option>
          {minutes.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        {/* AM/PM */}
        <select
          value={period}
          onChange={(e) => handleChange(hour || '12', minute || '00', e.target.value)}
          className="pl-2 pr-8 py-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none bg-white"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

// Utility function to format 24-hour time to 12-hour display
export const formatTime12 = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
};

export default TimePicker;
